const PREFIX_ROUTES = [
  ["/service-orders", "/service-orders"],
  ["/rastreio", "/rastreio"],
];

function pick(entry, lang) {
  if (!entry) return null;
  return entry[lang] || entry.pt;
}

export function matchHelpPath(pathname) {
  if (!pathname) return "/";
  const clean = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  if (clean === "/dashboard") return "/";
  for (const [prefix, key] of PREFIX_ROUTES) {
    if (clean === prefix || clean.startsWith(`${prefix}/`)) return key;
  }
  return clean;
}

export function resolvePageHelp(pathname, lang = "pt") {
  const key = matchHelpPath(pathname);
  return pick(PAGE_HELP[key] || PAGE_HELP.default, lang);
}

const PAGE_HELP = {
  "/login": {
    pt: {
      title: "Como entrar no sistema",
      description: "Acesso autenticado à plataforma Nafe Logistica.",
      sections: [
        {
          title: "O que é",
          body: "Tela de login corporativo. Cada usuário entra com e-mail e senha e o sistema abre o painel correspondente ao perfil (admin, operador, entregador ou cliente).",
        },
        {
          title: "Para que serve",
          body: "Proteger os dados do cartório e das intimações. Sem login, só a consulta pública de rastreio fica disponível.",
        },
        {
          title: "Como funciona",
          steps: [
            "Informe o e-mail cadastrado e a senha.",
            "Clique em Acessar painel.",
            "Se as credenciais estiverem corretas, você entra no módulo do seu perfil.",
            "Admin e operador vão para o Painel Executivo; entregador para a Central de Documentos; cliente para o Portal.",
          ],
        },
        {
          title: "Se não funcionar",
          items: [
            "Credenciais inválidas → confira e-mail/senha ou peça redefinição ao administrador.",
            "Conta inativa → o administrador precisa reativar o usuário em Usuários.",
            "Em homologação/local podem aparecer contas de demonstração; em produção use apenas sua conta real.",
          ],
        },
      ],
    },
    en: {
      title: "How to sign in",
      description: "Authenticated access to the Nafe Logistica platform.",
      sections: [
        {
          title: "What it is",
          body: "Corporate login. Each user signs in with email and password and lands on the screen for their role (admin, operator, deliverer, or client).",
        },
        {
          title: "What it is for",
          body: "Protects notary and intimations data. Without login, only the public tracking page is available.",
        },
        {
          title: "How it works",
          steps: [
            "Enter your registered email and password.",
            "Click Access dashboard.",
            "On success you enter the module for your role.",
            "Admin and operator go to the Executive Dashboard; deliverer to Documents; client to the Portal.",
          ],
        },
        {
          title: "If it does not work",
          items: [
            "Invalid credentials → check email/password or ask an admin to reset.",
            "Inactive account → an admin must reactivate the user in Users.",
            "Demo accounts may appear in homolog/local only; production uses real accounts.",
          ],
        },
      ],
    },
  },
  "/rastreio": {
    pt: {
      title: "Como rastrear um protocolo",
      description: "Consulta pública do status da intimação, sem precisar entrar no sistema.",
      sections: [
        {
          title: "O que é",
          body: "Página aberta para destinatários, cartórios ou clientes consultarem o andamento de um protocolo.",
        },
        {
          title: "Para que serve",
          body: "Dar visibilidade rápida (status, cidade e histórico recente) sem expor dados internos, evidências ou financeiro.",
        },
        {
          title: "Como funciona",
          steps: [
            "Digite o número ou protocolo.",
            "Pressione Enter ou o botão de busca.",
            "O sistema mostra status, cidade, última atualização e o histórico recente, se houver.",
          ],
        },
        {
          title: "Se não funcionar",
          items: [
            "Protocolo não encontrado → confira o número ou se o documento já foi importado.",
            "Não exige login; se precisar de detalhes (fotos, AR), use o Portal do Cliente com acesso autenticado.",
          ],
        },
      ],
    },
    en: {
      title: "How to track a protocol",
      description: "Public status lookup for an intimation, without signing in.",
      sections: [
        {
          title: "What it is",
          body: "Open page for recipients, notaries, or clients to check protocol progress.",
        },
        {
          title: "What it is for",
          body: "Quick visibility (status, city, recent history) without exposing internal data, evidence, or finance.",
        },
        {
          title: "How it works",
          steps: [
            "Enter the number or protocol.",
            "Press Enter or the search button.",
            "The system shows status, city, last update, and recent history when available.",
          ],
        },
        {
          title: "If it does not work",
          items: [
            "Not found → check the number or whether the document was imported.",
            "No login required; for photos/AR use the Client Portal with an account.",
          ],
        },
      ],
    },
  },
  "/": {
    pt: {
      title: "Como usar o Painel Executivo",
      description: "Visão geral da operação: KPIs, gráficos e alertas em tempo real.",
      sections: [
        {
          title: "O que é",
          body: "Tela inicial da operação. Mostra volume de documentos, entregas, devoluções e pontos de atenção do dia.",
        },
        {
          title: "Para que serve",
          body: "Acompanhar a saúde da operação sem abrir cada documento. Ideal para gestores e operadores no começo do turno.",
        },
        {
          title: "Como funciona",
          steps: [
            "Os KPIs do topo são em tempo real (Objeto Recebido, Em Rota de Entrega, Tentativa de entrega, Entregue, Devolvido, taxa de sucesso).",
            "Use os filtros de período, região e status nos gráficos (eles não alteram os KPIs).",
            "Leia os alertas inteligentes: documentos parados, rota longa, entrega sem evidência, financeiro vencido.",
            "Clique nos atalhos/alertas para ir à tela operacional correspondente.",
          ],
        },
      ],
    },
    en: {
      title: "How to use the Executive Dashboard",
      description: "Operations overview: KPIs, charts, and live alerts.",
      sections: [
        {
          title: "What it is",
          body: "Home screen of the operation. Shows document volume, deliveries, returns, and issues of the day.",
        },
        {
          title: "What it is for",
          body: "Track operational health without opening each document. Best for managers and operators at shift start.",
        },
        {
          title: "How it works",
          steps: [
            "Top KPIs are live (received, in route, delivered, returned, success rate).",
            "Use period, region, and status filters on the charts (they do not change KPIs).",
            "Read smart alerts: stalled documents, long routes, deliveries without evidence, overdue finance.",
            "Follow the shortcuts/alerts to the related operational screen.",
          ],
        },
      ],
    },
  },
  "/imports": {
    pt: {
      title: "Como homologar importações",
      description: "Entrada de intimações em lote (CSV, Excel, XML, PDF, TXT, JSON) com revisão antes de ir para a operação.",
      sections: [
        {
          title: "O que é",
          body: "Fila de lotes importados. Nada entra na Central de Documentos até alguém homologar (aprovar) as linhas.",
        },
        {
          title: "Para que serve",
          body: "Evitar cadastros errados: conferir destinatário, endereço e protocolo antes de soltar o documento para separação e rota.",
        },
        {
          title: "Como funciona",
          steps: [
            "Clique em Importar arquivo e envie o layout aceito (o mesmo do TXT de cartório).",
            "Abra Revisar no lote pendente.",
            "Marque as linhas corretas e aprove (parcial ou total).",
            "Linhas aprovadas viram documentos na operação; rejeitadas ficam de fora.",
            "Lotes também podem chegar automaticamente pelo e-mail do cartório.",
          ],
        },
      ],
    },
    en: {
      title: "How to homologate imports",
      description: "Batch intake (CSV, Excel, XML, PDF, TXT, JSON) with review before operations.",
      sections: [
        {
          title: "What it is",
          body: "Queue of imported batches. Nothing reaches Document Center until rows are approved.",
        },
        {
          title: "What it is for",
          body: "Prevent bad records: check recipient, address, and protocol before sorting and routing.",
        },
        {
          title: "How it works",
          steps: [
            "Click Import file and send an accepted layout (same as notary TXT).",
            "Open Review on a pending batch.",
            "Select correct rows and approve (partial or full).",
            "Approved rows become operational documents; rejected ones stay out.",
            "Batches can also arrive automatically from notary email.",
          ],
        },
      ],
    },
  },
  "/bipagem": {
    pt: {
      title: "Como usar a bipagem",
      description: "Vincule códigos de barras dos ARs aos protocolos dos documentos na central.",
      sections: [
        {
          title: "O que é",
          body: "A bipagem vincula o código de barras do AR (Aviso de Recebimento) ao protocolo do documento na central. O documento precisa existir no sistema (importação ou cadastro manual).",
        },
        {
          title: "Como fazer (passo a passo)",
          steps: [
            "Primeiro escolha o setor. Não atribuído mostra todos os entregadores; um setor mostra só os entregadores vinculados a ele.",
            "Não atribuído continua na lista de entregadores, com ou sem setor.",
            "Depois escolha o entregador e bipe os documentos (código de barras e/ou protocolo). O setor escolhido fica gravado na intimação.",
            "Para outro entregador, troque a seleção e continue bipando — só as próximas leituras vão para ele.",
            "Se bipar no entregador errado, corrija na lista de últimas bipagens (uma a uma ou várias de uma vez).",
          ],
        },
        {
          title: "Formatos aceitos",
          items: [
            "Protocolo: 12345-01/03/2026 ou variações com / e -",
            "Código de barras: padrão Correios (2 letras + números), ex.: AB123456789BR",
            "Códigos já vinculados ao mesmo par são reconhecidos e não duplicam o registro.",
          ],
        },
        {
          title: "Leitor USB",
          steps: [
            "Conecte o leitor no PC (a maioria funciona como teclado, sem instalar driver).",
            "Abra a Bipagem e clique no campo de leitura — ele já recebe foco automaticamente.",
            "Bipe o código do AR: o leitor digita os caracteres e envia Enter.",
            "Bipe o protocolo (ou faça na ordem inversa). Ouça o beep de sucesso.",
            "O campo limpa sozinho e volta a receber foco para a próxima bipagem.",
          ],
          items: [
            "Não precisa clicar em OK se o leitor envia Enter (padrão da maioria).",
            "Se o leitor não enviar Enter, o sistema confirma sozinho após ~220 ms com código completo.",
            "Use OK apenas se digitar manualmente ou se o leitor não mandar Enter no final.",
          ],
        },
        {
          title: "Câmera e atalhos",
          items: [
            "Botão da câmera: leitura por QR/código de barras no celular (Chrome/Edge).",
            "Enter ou OK: confirma o código digitado ou lido.",
            "Limpar ou Esc: cancela o par atual (código + protocolo) e recomeça.",
          ],
        },
        {
          title: "Conflitos",
          body: "Se o código já estiver em outro protocolo, ou o protocolo já tiver outro código, aparece um aviso. Você pode cancelar ou confirmar \"Substituir vínculo\" (admin/operador) — a alteração fica na auditoria e no histórico do documento.",
        },
        {
          title: "Se não funcionar",
          items: [
            "Nada acontece ao bipar → clique no campo de leitura antes (o leitor só funciona com foco nele).",
            "Código aparece mas não vincula → confira se o protocolo existe na Central de Documentos.",
            "Bipou o par errado → use Limpar ou a tecla Esc e recomece.",
            "Leitor digitando em outro lugar → feche ou tire o foco de outras janelas enquanto bipa.",
            "Protocolo não encontrado → documento precisa estar importado ou cadastrado no sistema.",
            "Acesso negado → usuário precisa ter perfil admin ou operador.",
            "Erro de ambiente → homolog e produção precisam apontar para o mesmo banco onde estão os documentos.",
          ],
        },
      ],
    },
    en: {
      title: "How to use AR scanning",
      description: "Link AR barcodes to document protocols in the center.",
      sections: [
        {
          title: "What it is",
          body: "Scanning binds the AR (return receipt) barcode to the document protocol. The document must already exist (import or manual entry).",
        },
        {
          title: "How to do it",
          steps: [
            "First choose the sector. Unassigned shows every deliverer; a sector shows only the deliverers linked to it.",
            "Unassigned stays in the deliverer list, with or without a sector.",
            "Then choose the deliverer and scan the documents (barcode and/or protocol). The chosen sector is saved on the document.",
            "For another deliverer, change the selection and keep scanning — only new reads go to them.",
            "If you scanned the wrong deliverer, fix it in the recent scans list (one or many at once).",
          ],
        },
        {
          title: "Accepted formats",
          items: [
            "Protocol: 12345-01/03/2026 or variations with / and -",
            "Barcode: Correios pattern (2 letters + numbers), e.g. AB123456789BR",
            "Codes already bound to the same pair are recognized and not duplicated.",
          ],
        },
        {
          title: "USB scanner",
          steps: [
            "Plug the scanner into the PC (most act as a keyboard, no driver).",
            "Open Scanning and click the read field — it auto-focuses.",
            "Scan the AR code: the device types characters and sends Enter.",
            "Scan the protocol (or reverse order). Listen for the success beep.",
            "The field clears and refocuses for the next pair.",
          ],
          items: [
            "No need to click OK if the scanner sends Enter.",
            "If it does not send Enter, the system confirms after ~220 ms with a complete code.",
            "Use OK only for manual typing or scanners without a trailing Enter.",
          ],
        },
        {
          title: "Camera and shortcuts",
          items: [
            "Camera button: QR/barcode on mobile (Chrome/Edge).",
            "Enter or OK: confirm typed or scanned code.",
            "Clear or Esc: cancel the current pair and start over.",
          ],
        },
        {
          title: "Conflicts",
          body: "If the code is already on another protocol, or the protocol already has another code, a warning appears. You can cancel or confirm Replace link (admin/operator) — the change is audited on the document history.",
        },
        {
          title: "If it does not work",
          items: [
            "Nothing happens → click the read field first.",
            "Code shows but does not bind → check that the protocol exists in Document Center.",
            "Wrong pair → Clear or Esc and restart.",
            "Scanner typing elsewhere → keep focus on this window.",
            "Protocol not found → document must be imported or registered.",
            "Access denied → admin or operator role required.",
            "Environment error → homolog and production must point to the same database as the documents.",
          ],
        },
      ],
    },
  },
  "/operational": {
    pt: {
      title: "Como usar a Central Operacional",
      description: "Kanban do fluxo da intimação, da homologação até a entrega ou devolução.",
      sections: [
        {
          title: "O que é",
          body: "Quadro de colunas com o status de cada documento: Objeto Recebido, Em Rota de Entrega, Tentativa de entrega, Entregue, Devolvido e Cancelado.",
        },
        {
          title: "Para que serve",
          body: "Mover a operação no dia a dia: aprovar, separar, vincular entregador e acompanhar tentativas sem abrir relatório.",
        },
        {
          title: "Como funciona",
          steps: [
            "Arraste o card entre colunas para atualizar o status (o histórico é gravado automaticamente).",
            "Cartões na coluna Cancelado ficam travados: só o administrador pode movê-los para outra coluna.",
            "Na importação, o cartório é preenchido automaticamente quando a cidade do documento coincide com um único cartório ativo.",
            "Use os filtros da tela para achar protocolo, cidade ou entregador.",
            "Abra o card para detalhe, evidências e ocorrências.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Operations",
      description: "Kanban of the intimation flow, from homologation to delivery or return.",
      sections: [
        {
          title: "What it is",
          body: "Column board of document status: object received, in route, attempted, delivered, returned, and cancelled.",
        },
        {
          title: "What it is for",
          body: "Run the daily operation: approve, sort, assign deliverer, and track attempts without opening a report.",
        },
        {
          title: "How it works",
          steps: [
            "Drag a card between columns to update status (history is saved automatically).",
            "Cards in the Canceled column stay locked: only an administrator can move them to another column.",
            "On import, the notary is filled automatically when the document city matches a single active tenant.",
            "Use filters to find protocol, city, or deliverer.",
            "Open the card for details, evidence, and occurrences.",
          ],
        },
      ],
    },
  },
  "/documents": {
    pt: {
      title: "Como usar a Central de Documentos",
      description: "Cadastro, busca e detalhe de cada intimação/protocolo.",
      sections: [
        {
          title: "O que é",
          body: "Lista mestra dos documentos do seu escopo (cartório, entregador ou cliente). É a ficha completa do protocolo.",
        },
        {
          title: "Para que serve",
          body: "Localizar um protocolo, corrigir dados, ver histórico, evidências e auditoria, criar documento avulso ou exportar a lista.",
        },
        {
          title: "Como funciona",
          steps: [
            "Busque por número, nome, CPF, endereço, cartório ou cidade.",
            "Filtre por status e entregador.",
            "Abra o documento para as abas Resumo, Histórico, Evidências e Auditoria.",
            "Admin/operador podem criar, editar, importar CSV e (admin) excluir com soft delete.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Document Center",
      description: "Registry, search, and detail of each intimation/protocol.",
      sections: [
        {
          title: "What it is",
          body: "Master list of documents in your scope (notary, deliverer, or client). Full protocol record.",
        },
        {
          title: "What it is for",
          body: "Find a protocol, fix data, view history, evidence and audit, create a one-off document, or export the list.",
        },
        {
          title: "How it works",
          steps: [
            "Search by number, name, tax id, address, notary, or city.",
            "Filter by status and deliverer.",
            "Open the document for Summary, History, Evidence, and Audit tabs.",
            "Admin/operator can create, edit, import CSV, and (admin) soft-delete.",
          ],
        },
      ],
    },
  },
  "/email-ingest": {
    pt: {
      title: "Como funciona o e-mail do cartório",
      description: "Entrada automática de intimações enviadas por e-mail com anexo TXT.",
      sections: [
        {
          title: "O que é",
          body: "Log do que chegou pelo canal de e-mail (Resend Receiving). O cartório envia mensagem com assunto Intimações e um TXT no mesmo layout da importação manual.",
        },
        {
          title: "Para que serve",
          body: "Não depender só do upload na tela de Homologação: o lote nasce sozinho e segue para revisão.",
        },
        {
          title: "Como funciona",
          steps: [
            "O e-mail precisa cair na caixa configurada (MX Resend ou encaminhamento).",
            "O sistema baixa o anexo, cria o lote e registra o log nesta tela.",
            "Use Buscar no Resend para forçar a coleta se o webhook atrasar.",
            "Depois homologhe o lote em Importação / Homologação.",
          ],
        },
      ],
    },
    en: {
      title: "How notary email ingest works",
      description: "Automatic intake of intimations sent by email with a TXT attachment.",
      sections: [
        {
          title: "What it is",
          body: "Log of what arrived via email (Resend Receiving). The notary sends subject Intimações plus a TXT in the same layout as manual import.",
        },
        {
          title: "What it is for",
          body: "Avoid relying only on Homologation upload: the batch is created automatically and waits for review.",
        },
        {
          title: "How it works",
          steps: [
            "Mail must reach the configured inbox (Resend MX or forwarding).",
            "The system downloads the attachment, creates the batch, and logs it here.",
            "Use Fetch from Resend to poll if the webhook is late.",
            "Then homologate the batch in Import / Homologation.",
          ],
        },
      ],
    },
  },
  "/routes": {
    pt: {
      title: "Como usar a Gestão de Rotas",
      description: "Mapa e organização das entregas no território.",
      sections: [
        {
          title: "O que é",
          body: "Visão geográfica dos documentos com latitude/longitude e das rotas montadas para os entregadores.",
        },
        {
          title: "Para que serve",
          body: "Planejar o dia: ver concentração por bairro, montar rota e acompanhar quem está em campo.",
        },
        {
          title: "Como funciona",
          steps: [
            "O mapa mostra marcadores coloridos por status.",
            "Abra o popup para ver protocolo, destinatário e endereço.",
            "Crie ou edite rotas associando documentos e entregador.",
            "Documentos sem geolocalização não aparecem no mapa até o CEP/endereço ser resolvido.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Route Management",
      description: "Map and organization of deliveries in the territory.",
      sections: [
        {
          title: "What it is",
          body: "Geographic view of documents with lat/lng and of routes built for deliverers.",
        },
        {
          title: "What it is for",
          body: "Plan the day: see density by neighborhood, build a route, and follow who is in the field.",
        },
        {
          title: "How it works",
          steps: [
            "The map shows markers colored by status.",
            "Open the popup for protocol, recipient, and address.",
            "Create or edit routes by linking documents and a deliverer.",
            "Documents without geolocation stay off the map until CEP/address is resolved.",
          ],
        },
      ],
    },
  },
  "/tracking": {
    pt: {
      title: "Como usar o rastreio ao vivo",
      description: "Posição recente dos entregadores e destinos em rota.",
      sections: [
        {
          title: "O que é",
          body: "Mapa operacional com pings de GPS dos entregadores (últimos minutos) e marcadores das intimações em rota.",
        },
        {
          title: "Para que serve",
          body: "A central ver quem está em campo, se o GPS está atualizando e onde está o destino, sem ligar para o entregador.",
        },
        {
          title: "Como funciona",
          steps: [
            "O mapa atualiza sozinho (cerca de 15 segundos).",
            "Cada entregador ativo mostra nome, precisão e horário do último ping.",
            "A tabela resume quem está visível no mapa.",
            "O ping só existe enquanto o app do entregador está aberto no celular com permissão de localização.",
          ],
        },
      ],
    },
    en: {
      title: "How to use live tracking",
      description: "Recent deliverer positions and in-route destinations.",
      sections: [
        {
          title: "What it is",
          body: "Ops map with GPS pings (last minutes) and markers for intimations out for delivery.",
        },
        {
          title: "What it is for",
          body: "Let the desk see who is in the field and whether GPS is updating, without calling the deliverer.",
        },
        {
          title: "How it works",
          steps: [
            "The map refreshes on its own (~15 seconds).",
            "Each active deliverer shows name, accuracy, and last ping time.",
            "The table lists who is visible on the map.",
            "Pings only exist while the Deliverer app is open on the phone with location permission.",
          ],
        },
      ],
    },
  },
  "/deliverer": {
    pt: {
      title: "Como usar o App do Entregador",
      description: "Tela mobile das entregas do dia: mapa, lista, navegação e registro de ocorrência.",
      sections: [
        {
          title: "O que é",
          body: "Aplicativo interno (PWA) do entregador. Mostra só as intimações atribuídas a ele, com GPS e evidências.",
        },
        {
          title: "Para que serve",
          body: "Navegar até o endereço, registrar entrega/tentativa/devolução com foto e localização — inclusive offline.",
        },
        {
          title: "Como funciona",
          steps: [
            "Use a aba Mapa (posição atual + destinos) ou Lista.",
            "Toque em Navegar para abrir o Google Maps.",
            "Toque em Ocorrência/Registrar e complete recebedor, motivo e localização.",
            "Anexe foto ou AR se a operação exigir.",
            "Confirme: se estiver sem rede, a ocorrência fica na fila e sincroniza depois.",
          ],
        },
        {
          title: "Localização",
          items: [
            "Prefira GPS nativo (mais preciso para prova jurídica).",
            "Se o GPS falhar, o sistema usa o último ponto em cache ou estimativa por IP.",
            "Mantenha esta tela aberta para o rastreio ao vivo da central funcionar.",
          ],
        },
      ],
    },
    en: {
      title: "How to use the Deliverer app",
      description: "Mobile screen for today's deliveries: map, list, navigation, and occurrence capture.",
      sections: [
        {
          title: "What it is",
          body: "Internal PWA for the deliverer. Shows only intimations assigned to them, with GPS and evidence.",
        },
        {
          title: "What it is for",
          body: "Navigate to the address and register delivery/attempt/return with photo and location — including offline.",
        },
        {
          title: "How it works",
          steps: [
            "Use Map (current position + destinations) or List.",
            "Tap Navigate to open Google Maps.",
            "Tap Occurrence/Register and fill recipient, reason, and location.",
            "Attach a photo or AR if required.",
            "Confirm: if offline, the occurrence queues and syncs later.",
          ],
        },
        {
          title: "Location",
          items: [
            "Prefer native GPS (better legal proof).",
            "If GPS fails, the app uses cached point or IP estimate.",
            "Keep this screen open so live tracking at the desk works.",
          ],
        },
      ],
    },
  },
  "/service-orders": {
    pt: {
      title: "Como usar Ordem de Serviço",
      description: "OS de transporte de carga: preenchimento pelo entregador e aprovação na central.",
      sections: [
        {
          title: "O que é",
          body: "Formulário de ordem de serviço (coleta, entrega, km e cliente pagador), separado das intimações de cartório.",
        },
        {
          title: "Para que serve",
          body: "Registrar fretes avulsos, calcular valor (quando aplicável) e gerar conta a receber após aprovação.",
        },
        {
          title: "Como funciona",
          steps: [
            "Entregador preenche endereços (use + para mais coletas/entregas) e envia.",
            "Somente o administrador altera o valor do frete. O operador vê o valor, revisa e aprova.",
            "Na aprovação, entra um lançamento em Contas a Receber.",
            "Use o histórico e a exportação (CSV/Excel/PDF) para conferência.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Service Orders",
      description: "Cargo transport OS: filled by the deliverer and approved at the desk.",
      sections: [
        {
          title: "What it is",
          body: "Service-order form (pickup, delivery, km, paying client), separate from notary intimations.",
        },
        {
          title: "What it is for",
          body: "Register extra freights, calculate amount when applicable, and create a receivable after approval.",
        },
        {
          title: "How it works",
          steps: [
            "Deliverer fills addresses (use + for extra stops) and submits.",
            "Only the administrator can change the freight value. The operator sees the value, reviews, and approves.",
            "On approval, a receivable is created.",
            "Use history and export (CSV/Excel/PDF) for control.",
          ],
        },
      ],
    },
  },
  "/evidences": {
    pt: {
      title: "Como usar a Central de Evidências",
      description: "Galeria das provas de entrega: foto, assinatura, AR, recibo e documento.",
      sections: [
        {
          title: "O que é",
          body: "Repositório das evidências anexadas às intimações, com metadados de GPS, usuário e data.",
        },
        {
          title: "Para que serve",
          body: "Conferir se a entrega tem prova jurídica e baixar o arquivo para o cartório ou cliente.",
        },
        {
          title: "Como funciona",
          steps: [
            "Filtre por tipo (foto, assinatura, AR, recibo, documento).",
            "Abra o card para ver protocolo, entregador e se há GPS.",
            "Use o download individual quando precisar arquivar fora do sistema.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Evidence Center",
      description: "Gallery of delivery proof: photo, signature, AR, receipt, and document.",
      sections: [
        {
          title: "What it is",
          body: "Repository of files attached to intimations, with GPS, user, and date metadata.",
        },
        {
          title: "What it is for",
          body: "Check whether a delivery has legal proof and download the file for the notary or client.",
        },
        {
          title: "How it works",
          steps: [
            "Filter by type (photo, signature, AR, receipt, document).",
            "Open the card for protocol, deliverer, and GPS.",
            "Use individual download when you need to archive outside the system.",
          ],
        },
      ],
    },
  },
  "/portal": {
    pt: {
      title: "Como usar o Portal do Cliente",
      description: "Visão simplificada dos protocolos vinculados à sua conta.",
      sections: [
        {
          title: "O que é",
          body: "Painel externo do cliente (parte interessada). Mostra apenas os documentos em que você é o cliente do protocolo.",
        },
        {
          title: "Para que serve",
          body: "Acompanhar totais, o que está em rota, entregues e devolvidos sem acessar a operação interna do cartório.",
        },
        {
          title: "Como funciona",
          steps: [
            "Veja os KPIs no topo.",
            "Abra um protocolo na lista para status e histórico.",
            "Admin também pode entrar aqui para simular a visão do cliente.",
          ],
        },
      ],
    },
    en: {
      title: "How to use the Client Portal",
      description: "Simplified view of protocols linked to your account.",
      sections: [
        {
          title: "What it is",
          body: "External client dashboard. Shows only documents where you are the protocol client.",
        },
        {
          title: "What it is for",
          body: "Follow totals, in-route, delivered, and returned items without internal notary operations.",
        },
        {
          title: "How it works",
          steps: [
            "Read the KPIs at the top.",
            "Open a protocol in the list for status and history.",
            "Admins can also open this screen to preview the client view.",
          ],
        },
      ],
    },
  },
  "/financial": {
    pt: {
      title: "Como usar o Financeiro",
      description: "Contas a pagar e a receber, categorias e fluxo de caixa.",
      sections: [
        {
          title: "O que é",
          body: "Módulo corporativo de caixa: visão geral, recebíveis, pagáveis e categorias.",
        },
        {
          title: "Para que serve",
          body: "Controlar saldo realizado e projetado, lançar títulos, marcar como pago e anexar comprovantes.",
        },
        {
          title: "Como funciona",
          steps: [
            "Na Visão Geral, escolha o período do gráfico e dos totais.",
            "Em Contas a Receber/Pagar, crie lançamentos com vencimento, categoria e parte.",
            "Marque como pago quando o dinheiro sair ou entrar.",
            "Ordens de serviço aprovadas podem gerar recebível automaticamente.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Finance",
      description: "Payables, receivables, categories, and cash flow.",
      sections: [
        {
          title: "What it is",
          body: "Corporate cash module: overview, receivables, payables, and categories.",
        },
        {
          title: "What it is for",
          body: "Track realized and projected balance, post entries, mark as paid, and attach receipts.",
        },
        {
          title: "How it works",
          steps: [
            "In Overview, pick the period for the chart and totals.",
            "In Receivables/Payables, create entries with due date, category, and party.",
            "Mark as paid when money moves.",
            "Approved service orders may create a receivable automatically.",
          ],
        },
      ],
    },
  },
  "/commissions": {
    pt: {
      title: "Como usar Comissões",
      description: "Regras por cidade, apuração mensal e pagamento ao entregador.",
      sections: [
        {
          title: "O que é",
          body: "Motor de comissão das intimações. Usa a cidade do documento (cadastro/importação), não a ocorrência de GPS.",
        },
        {
          title: "Para que serve",
          body: "Calcular o que cada entregador tem a receber no mês (faixa fixa + excedente) e registrar o pagamento.",
        },
        {
          title: "Como funciona",
          steps: [
            "Admin configura cidades, modo (por documento ou endereço), valor e limiar mensal.",
            "Ao entregar, o sistema recalcula a competência do mês.",
            "Acompanhe o relatório por cidade e a lista de comissões pendentes/pagas.",
            "Admin/operador podem marcar como paga.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Commissions",
      description: "City rules, monthly closing, and deliverer payout.",
      sections: [
        {
          title: "What it is",
          body: "Commission engine for intimations. Uses the document city (registry/import), not the GPS occurrence.",
        },
        {
          title: "What it is for",
          body: "Calculate what each deliverer earned in the month (fixed band + excess) and record payment.",
        },
        {
          title: "How it works",
          steps: [
            "Admin sets cities, mode (per document or address), rate, and monthly threshold.",
            "On delivery, the system recalculates the month.",
            "Follow the city report and pending/paid list.",
            "Admin/operator can mark as paid.",
          ],
        },
      ],
    },
  },
  "/productivity": {
    pt: {
      title: "Como ler a Produtividade",
      description: "Ranking dos entregadores nos últimos 30 dias.",
      sections: [
        {
          title: "O que é",
          body: "Painel comparativo: entregues, tentativas, taxa de sucesso e comissões acumuladas por entregador.",
        },
        {
          title: "Para que serve",
          body: "Ver quem está produzindo, quem tem muita tentativa sem conclusão e o reflexo financeiro.",
        },
        {
          title: "Como funciona",
          body: "Os cards atualizam automaticamente. Não há lançamento manual nesta tela — os números vêm das entregas e comissões já registradas.",
        },
      ],
    },
    en: {
      title: "How to read Productivity",
      description: "Deliverer ranking for the last 30 days.",
      sections: [
        {
          title: "What it is",
          body: "Comparison board: delivered, attempts, success rate, and commission totals per deliverer.",
        },
        {
          title: "What it is for",
          body: "See who is producing, who has many unfinished attempts, and the financial impact.",
        },
        {
          title: "How it works",
          body: "Cards refresh automatically. There is no manual posting here — numbers come from deliveries and commissions already recorded.",
        },
      ],
    },
  },
  "/goals": {
    pt: {
      title: "Como usar Metas de entrega",
      description: "Campanhas de volume: defina a meta, acompanhe o progresso e gere bônus em Comissões.",
      sections: [
        {
          title: "O que é",
          body: "Controle de metas para entregadores. Cada meta tem um alvo de entregas, um prazo em dias e um valor de bônus. Só o administrador acessa esta tela.",
        },
        {
          title: "Para que serve",
          body: "Estimular volume de entregas no período. Quem atinge a meta recebe um bônus extra lançado automaticamente em Comissões, vinculado ao mês avaliado.",
        },
        {
          title: "Como lançar uma meta",
          steps: [
            "Dê um nome interno (ex.: Meta Julho — Centro).",
            "Escolha um entregador ou deixe em branco para valer para todos.",
            "Informe quantas entregas concluídas são necessárias, por quantos dias a meta fica ativa e o valor do bônus em R$.",
            "Clique em Lançar meta. O prazo começa na data do lançamento.",
          ],
        },
        {
          title: "Como avaliar e pagar o bônus",
          steps: [
            "Escolha o mês de referência (período de avaliação).",
            "Clique em Avaliar metas do período.",
            "O sistema conta as entregas daquele mês. Quem bateu a meta recebe o bônus em Comissões.",
            "Metas inativas ou já encerradas pelo prazo não entram na avaliação.",
          ],
        },
        {
          title: "Status e edição",
          items: [
            "Ativa: dentro do prazo e habilitada para avaliação.",
            "Encerrada: passou o número de dias disponíveis.",
            "Inativa: desligada manualmente; não gera bônus.",
            "Editar recalcula o prazo se você mudar os dias disponíveis.",
            "O progresso no card mostra entregas do período e se o bônus já foi pago.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Delivery goals",
      description: "Volume campaigns: set a target, track progress, and create bonuses in Commissions.",
      sections: [
        {
          title: "What it is",
          body: "Goal control for deliverers. Each goal has a delivery target, a duration in days, and a bonus amount. Only admins can open this screen.",
        },
        {
          title: "What it is for",
          body: "Drive delivery volume in a period. Whoever hits the goal gets an extra bonus posted automatically in Commissions, tied to the evaluated month.",
        },
        {
          title: "How to launch a goal",
          steps: [
            "Give it an internal name (e.g. July goal — Downtown).",
            "Pick one deliverer or leave blank to apply to everyone.",
            "Set required completed deliveries, how many days it stays active, and the bonus in R$.",
            "Click Launch goal. The window starts on the launch date.",
          ],
        },
        {
          title: "How to evaluate and pay the bonus",
          steps: [
            "Choose the reference month (evaluation period).",
            "Click Evaluate period goals.",
            "The system counts deliveries in that month. Whoever hit the goal gets the bonus in Commissions.",
            "Inactive or expired goals are skipped.",
          ],
        },
        {
          title: "Status and editing",
          items: [
            "Active: still within the window and enabled for evaluation.",
            "Closed: the available days have passed.",
            "Inactive: turned off manually; no bonus is created.",
            "Editing recalculates the window if you change available days.",
            "Card progress shows period deliveries and whether the bonus was already paid.",
          ],
        },
      ],
    },
  },
  "/audit": {
    pt: {
      title: "Como usar a Auditoria",
      description: "Trilha imutável das ações críticas do sistema.",
      sections: [
        {
          title: "O que é",
          body: "Log de quem fez o quê: login, documentos, evidências, financeiro, rotas, usuários, webhooks etc.",
        },
        {
          title: "Para que serve",
          body: "Compliance e investigação: reconstruir o estado anterior/posterior de um registro, com IP e horário.",
        },
        {
          title: "Como funciona",
          steps: [
            "Filtre por módulo, período ou texto.",
            "Abra o evento para ver before/after sanitizados.",
            "Nada pode ser apagado pela API — a trilha é permanente.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Audit",
      description: "Immutable trail of critical system actions.",
      sections: [
        {
          title: "What it is",
          body: "Log of who did what: login, documents, evidence, finance, routes, users, webhooks, and more.",
        },
        {
          title: "What it is for",
          body: "Compliance and investigation: reconstruct before/after of a record, with IP and timestamp.",
        },
        {
          title: "How it works",
          steps: [
            "Filter by module, period, or text.",
            "Open an event to see sanitized before/after.",
            "Nothing can be deleted via the API — the trail is permanent.",
          ],
        },
      ],
    },
  },
  "/occurrences": {
    pt: {
      title: "Como usar o catálogo de ocorrências",
      description: "Motivos padronizados que o entregador escolhe no campo.",
      sections: [
        {
          title: "O que é",
          body: "Lista de códigos e descrições (ex.: AUS — Destinatário ausente) por tipo: entregue ou ocorrência/tentativa.",
        },
        {
          title: "Para que serve",
          body: "Evitar texto livre inconsistente nos relatórios e nas evidências jurídicas.",
        },
        {
          title: "Como funciona",
          steps: [
            "Cadastre código curto e descrição clara.",
            "Escolha o tipo (Entregue ou Ocorrência).",
            "Edite ou exclua itens que não devem mais aparecer no app do entregador.",
          ],
        },
      ],
    },
    en: {
      title: "How to use the occurrence catalog",
      description: "Standard reasons the deliverer picks in the field.",
      sections: [
        {
          title: "What it is",
          body: "List of codes and labels (e.g. AUS — Recipient absent) by type: delivered or occurrence/attempt.",
        },
        {
          title: "What it is for",
          body: "Avoid inconsistent free text in reports and legal evidence.",
        },
        {
          title: "How it works",
          steps: [
            "Register a short code and a clear label.",
            "Choose the type (Delivered or Occurrence).",
            "Edit or delete items that should no longer appear in the deliverer app.",
          ],
        },
      ],
    },
  },
  "/users": {
    pt: {
      title: "Como gerenciar usuários",
      description: "Contas, perfis (RBAC) e vínculo com cartório/operador.",
      sections: [
        {
          title: "O que é",
          body: "Cadastro de quem acessa o sistema: administrador, operador, entregador e cliente.",
        },
        {
          title: "Para que serve",
          body: "Controlar permissão e isolamento de dados. Operador liga-se a um ou mais cartórios; entregador precisa de cartório e de um operador responsável.",
        },
        {
          title: "Como funciona",
          steps: [
            "Crie o usuário com nome, e-mail, senha e perfil.",
            "Operador: marque os cartórios (tenant_ids).",
            "Entregador: escolha cartório e operador.",
            "Cliente: escolha o cartório; ele só verá documentos em que for o client_id.",
            "Desative em vez de apagar quando a pessoa sair da operação.",
          ],
        },
      ],
    },
    en: {
      title: "How to manage users",
      description: "Accounts, roles (RBAC), and notary/operator links.",
      sections: [
        {
          title: "What it is",
          body: "Registry of who can access the system: admin, operator, deliverer, and client.",
        },
        {
          title: "What it is for",
          body: "Control permission and data isolation. Operators link to one or more notaries; deliverers need a notary and a responsible operator.",
        },
        {
          title: "How it works",
          steps: [
            "Create the user with name, email, password, and role.",
            "Operator: select notaries (tenant_ids).",
            "Deliverer: pick notary and operator.",
            "Client: pick notary; they only see documents where they are client_id.",
            "Deactivate instead of deleting when someone leaves.",
          ],
        },
      ],
    },
  },
  "/sectors": {
    pt: {
      title: "Como cadastrar setores",
      description: "Setores de entrega por cartório, com os entregadores já existentes.",
      sections: [
        {
          title: "O que é",
          body: "Cada cartório tem quantos setores forem necessários (ex.: Centro, Morada do Sol). Cada setor reúne um ou mais entregadores.",
        },
        {
          title: "Para que serve",
          body: "Organizar a operação por região dentro do cartório. O mesmo entregador pode estar em mais de um setor.",
        },
        {
          title: "Como funciona",
          steps: [
            "Escolha um cartório já cadastrado.",
            "Informe o nome do setor. O nome é livre e pode ser criado quantas vezes for preciso.",
            "Marque os entregadores já cadastrados em Usuários.",
            "Edite o nome ou os vínculos depois, ou exclua o setor.",
          ],
        },
      ],
    },
    en: {
      title: "How to register sectors",
      description: "Delivery sectors per notary office, using existing deliverers.",
      sections: [
        {
          title: "What it is",
          body: "Each notary office can have as many sectors as needed (e.g. Downtown, Morada do Sol). Each sector groups one or more deliverers.",
        },
        {
          title: "What it is for",
          body: "Organize operations by area inside the notary office. The same deliverer can belong to more than one sector.",
        },
        {
          title: "How it works",
          steps: [
            "Pick a notary office that already exists.",
            "Type the sector name. Names are free text and you can add as many as you need.",
            "Check the deliverers already registered in Users.",
            "Edit the name or the links later, or delete the sector.",
          ],
        },
      ],
    },
  },
  "/tenants": {
    pt: {
      title: "Como gerenciar cartórios",
      description: "Tenants (cartórios) isolados no mesmo sistema.",
      sections: [
        {
          title: "O que é",
          body: "Cadastro de cartórios: nome, CNPJ, e-mail, cidade e se está ativo.",
        },
        {
          title: "Para que serve",
          body: "Separar dados por cartório e preencher automaticamente o cartório na importação quando a cidade do documento coincidir com um único cartório ativo.",
        },
        {
          title: "Como funciona",
          steps: [
            "Crie ou edite o cartório e a cidade (usada no match da importação).",
            "A vinculação de pessoas é feita em Usuários, não nesta tela.",
            "Setores de entrega de cada cartório ficam em Setores.",
            "Se mudar o nome, os documentos podem sincronizar o campo de cartório.",
          ],
        },
      ],
    },
    en: {
      title: "How to manage notaries",
      description: "Isolated tenants (notaries) in the same system.",
      sections: [
        {
          title: "What it is",
          body: "Notary registry: name, tax id, email, city, and active flag.",
        },
        {
          title: "What it is for",
          body: "Split data per notary and auto-fill the notary on import when the document city matches a single active tenant.",
        },
        {
          title: "How it works",
          steps: [
            "Create or edit the notary and city (used in the import match).",
            "People are linked in Users, not on this screen.",
            "Delivery sectors for each notary live in Sectors.",
            "If you rename, documents may sync the notary field.",
          ],
        },
      ],
    },
  },
  "/webhooks": {
    pt: {
      title: "Como usar Webhooks",
      description: "Avisos HMAC para sistemas externos quando algo muda na operação.",
      sections: [
        {
          title: "O que é",
          body: "Cadastro de URLs que recebem eventos (documento, evidência, etc.) assinados com HMAC-SHA256.",
        },
        {
          title: "Para que serve",
          body: "Integrar ERP, Zapier ou o sistema do cartório sem consulta manual.",
        },
        {
          title: "Como funciona",
          steps: [
            "Crie o webhook com URL, eventos e, se quiser, um cartório (sem cartório = global).",
            "Dispare um teste e abra os logs de entrega.",
            "Valide a assinatura no destino usando o segredo gerado.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Webhooks",
      description: "HMAC notifications to external systems when operations change.",
      sections: [
        {
          title: "What it is",
          body: "Registry of URLs that receive events (document, evidence, etc.) signed with HMAC-SHA256.",
        },
        {
          title: "What it is for",
          body: "Integrate ERP, Zapier, or the notary system without manual polling.",
        },
        {
          title: "How it works",
          steps: [
            "Create a webhook with URL, events, and optional notary (none = global).",
            "Send a test and open delivery logs.",
            "Validate the signature at the destination with the generated secret.",
          ],
        },
      ],
    },
  },
  "/security": {
    pt: {
      title: "Como usar a Central de Segurança",
      description: "Sessões, senha e direitos de privacidade (LGPD).",
      sections: [
        {
          title: "O que é",
          body: "Área da sua conta: dispositivos logados, troca de senha e solicitações LGPD.",
        },
        {
          title: "Para que serve",
          body: "Encerrar sessão em outro aparelho, reforçar a senha e exercer direitos de acesso/exclusão de dados pessoais.",
        },
        {
          title: "Como funciona",
          steps: [
            "Em Sessões ativas, veja onde sua conta está aberta e encerre as que não reconhecer.",
            "Em Senha, informe a atual e a nova.",
            "Em Privacidade, use as opções LGPD conforme a política da operação.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Security Center",
      description: "Sessions, password, and privacy rights (LGPD).",
      sections: [
        {
          title: "What it is",
          body: "Your account area: logged devices, password change, and LGPD requests.",
        },
        {
          title: "What it is for",
          body: "Sign out of another device, strengthen the password, and exercise access/erasure rights.",
        },
        {
          title: "How it works",
          steps: [
            "In Active sessions, review where the account is open and revoke unknown ones.",
            "In Password, enter the current and new password.",
            "In Privacy, use LGPD options according to the operation policy.",
          ],
        },
      ],
    },
  },
  "/notifications": {
    pt: {
      title: "Como usar Notificações",
      description: "Central de avisos do sistema (broadcast ou direcionados a você).",
      sections: [
        {
          title: "O que é",
          body: "Caixa de mensagens internas: informativo, alerta, sucesso ou crítico.",
        },
        {
          title: "Para que serve",
          body: "Não perder recado operacional (lote homologado, pendência, aviso da central).",
        },
        {
          title: "Como funciona",
          steps: [
            "Abra a lista e leia o conteúdo.",
            "Marque como lida individualmente.",
            "Em Configurações você pode ativar push no navegador, se o ambiente permitir.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Notifications",
      description: "In-app inbox (broadcast or targeted to you).",
      sections: [
        {
          title: "What it is",
          body: "Internal messages: info, warning, success, or critical.",
        },
        {
          title: "What it is for",
          body: "Do not miss operational notes (homologated batch, pending item, desk warning).",
        },
        {
          title: "How it works",
          steps: [
            "Open the list and read the content.",
            "Mark as read individually.",
            "In Settings you can enable browser push if the environment allows it.",
          ],
        },
      ],
    },
  },
  "/reports": {
    pt: {
      title: "Como gerar Relatórios",
      description: "Exportações de operação, gestão, financeiro e jurídico.",
      sections: [
        {
          title: "O que é",
          body: "Central de relatórios prontos (posição operacional, ocorrências, SLA, comissões, evidências, auditoria etc.).",
        },
        {
          title: "Para que serve",
          body: "Fechamento, prestação de contas ao cartório e análise fora do sistema (Excel/PDF).",
        },
        {
          title: "Como funciona",
          steps: [
            "Escolha a área (operação, gestão, financeiro ou jurídico).",
            "Selecione o relatório e preencha os filtros (período, status, entregador, busca).",
            "Exporte em CSV, Excel ou PDF.",
            "CSV é o mais compatível; Excel vem formatado; PDF serve para envio formal.",
          ],
        },
      ],
    },
    en: {
      title: "How to generate Reports",
      description: "Exports for operations, management, finance, and legal.",
      sections: [
        {
          title: "What it is",
          body: "Ready-made reports (operational position, occurrences, SLA, commissions, evidence, audit, etc.).",
        },
        {
          title: "What it is for",
          body: "Month-end, notary accountability, and analysis outside the system (Excel/PDF).",
        },
        {
          title: "How it works",
          steps: [
            "Pick an area (operations, management, finance, or legal).",
            "Select the report and fill filters (period, status, deliverer, search).",
            "Export as CSV, Excel, or PDF.",
            "CSV is most compatible; Excel is formatted; PDF is for formal sending.",
          ],
        },
      ],
    },
  },
  "/settings": {
    pt: {
      title: "Como usar Configurações",
      description: "Preferências da sua conta: tema, idioma, perfil e notificações push.",
      sections: [
        {
          title: "O que é",
          body: "Tela pessoal. Não altera regras do cartório — só a experiência do usuário logado.",
        },
        {
          title: "Para que serve",
          body: "Ajustar aparência, idioma (PT/EN) e se o navegador pode receber push.",
        },
        {
          title: "Como funciona",
          steps: [
            "Confira nome, e-mail e perfil (somente leitura aqui; cadastro fica em Usuários).",
            "Alterne tema claro/escuro e idioma.",
            "Ative ou desative notificações push se o navegador suportar.",
            "Senha e sessões ficam na Central de Segurança.",
          ],
        },
      ],
    },
    en: {
      title: "How to use Settings",
      description: "Account preferences: theme, language, profile, and push notifications.",
      sections: [
        {
          title: "What it is",
          body: "Personal screen. It does not change notary rules — only the logged-in user's experience.",
        },
        {
          title: "What it is for",
          body: "Adjust appearance, language (PT/EN), and whether the browser may receive push.",
        },
        {
          title: "How it works",
          steps: [
            "Check name, email, and role (read-only here; registry is in Users).",
            "Toggle light/dark theme and language.",
            "Enable or disable push if the browser supports it.",
            "Password and sessions live in Security Center.",
          ],
        },
      ],
    },
  },
  default: {
    pt: {
      title: "Ajuda desta tela",
      description: "Guia rápido da plataforma Nafe Logistica.",
      sections: [
        {
          title: "O que é",
          body: "Sistema de gestão de intimações, rotas, evidências, financeiro e auditoria para cartórios e operação de entrega.",
        },
        {
          title: "Como navegar",
          items: [
            "O menu à esquerda abre cada módulo conforme o seu perfil.",
            "Use ? Ajuda em qualquer tela para ver o que ela faz e o passo a passo.",
            "Em caso de erro de permissão, peça ao administrador o perfil adequado.",
          ],
        },
      ],
    },
    en: {
      title: "Help for this screen",
      description: "Quick guide to the Nafe Logistica platform.",
      sections: [
        {
          title: "What it is",
          body: "Platform for intimations, routes, evidence, finance, and audit for notaries and delivery operations.",
        },
        {
          title: "How to navigate",
          items: [
            "The left menu opens each module according to your role.",
            "Use ? Help on any screen to see what it does and the steps.",
            "On permission errors, ask an admin for the right role.",
          ],
        },
      ],
    },
  },
};
